// src/components/admin/page-management/section-forms/FeaturesForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const FeaturesForm = ({ content, updateContent }) => {
  const [expandedFeatureIndex, setExpandedFeatureIndex] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Initialize features if not present
  const features = content?.features || [
    {
      icon: 'star',
      title: 'Premium Quality',
      description: 'All our products are crafted with premium materials for exceptional durability and presentation.'
    },
    {
      icon: 'leaf',
      title: 'Eco-Friendly',
      description: 'We prioritize sustainable materials and manufacturing processes to minimize environmental impact.'
    },
    {
      icon: 'award',
      title: 'Custom Designs',
      description: 'Our design team works with you to create unique packaging that perfectly represents your brand.'
    },
    {
      icon: 'truck',
      title: 'Fast Delivery',
      description: 'With our efficient production and shipping, your orders will arrive right when you need them.'
    }
  ];

  // Add a new feature
  const addFeature = () => {
    const newFeatures = [...features, {
      icon: 'plus',
      title: 'New Feature',
      description: 'Description for the new feature'
    }];

    updateContent('features', newFeatures);
    setExpandedFeatureIndex(newFeatures.length - 1);
  };

  // Remove a feature
  const removeFeature = (index) => {
    const newFeatures = [...features];
    newFeatures.splice(index, 1);
    updateContent('features', newFeatures);

    if (expandedFeatureIndex === index) {
      setExpandedFeatureIndex(null);
    } else if (expandedFeatureIndex > index) {
      setExpandedFeatureIndex(expandedFeatureIndex - 1);
    }
  };

  // Update a feature property
  const updateFeature = (index, property, value) => {
    const newFeatures = [...features];
    newFeatures[index] = {
      ...newFeatures[index],
      [property]: value
    };
    updateContent('features', newFeatures);
  };

  // Move a feature up or down
  const moveFeature = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === features.length - 1)
    ) {
      return;
    }

    const newFeatures = [...features];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    [newFeatures[index], newFeatures[newIndex]] = [newFeatures[newIndex], newFeatures[index]];

    updateContent('features', newFeatures);

    // Update expanded index if needed
    if (expandedFeatureIndex === index) {
      setExpandedFeatureIndex(newIndex);
    } else if (expandedFeatureIndex === newIndex) {
      setExpandedFeatureIndex(index);
    }
  };

  const iconOptions = [
    'star', 'leaf', 'award', 'truck', 'shield', 'globe', 'clock', 'heart', 'plus', 'no icon'
  ];

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Features Section</h3>

      {/* Section title and subtitle */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Content</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Section Title</label>
          <input
            type="text"
            value={content?.title || 'Why Choose Bag & Box?'}
            onChange={(e) => updateContent('title', e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Section Subtitle</label>
          <textarea
            value={content?.subtitle || 'We combine quality, innovation, and sustainability to deliver the perfect packaging solution for your brand.'}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            className="w-full p-2 border rounded"
            rows="2"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title Color</label>
            <ColorPicker
              color={content?.titleColor || '#111827'}
              onChange={(color) => updateContent('titleColor', color)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle Color</label>
            <ColorPicker
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
          </div>
        </div>
      </div>

      {/* Features list */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Features</h4>

        <div className="space-y-3">
          {features.map((feature, index) => (
            <div
              key={index}
              className="border rounded bg-white overflow-hidden"
            >
              <div
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedFeatureIndex(expandedFeatureIndex === index ? null : index)}
              >
                <div className="flex items-center">
                  <span className="font-medium">{feature.title || `Feature ${index + 1}`}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveFeature(index, 'up');
                    }}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveFeature(index, 'down');
                    }}
                    disabled={index === features.length - 1}
                  >
                    <ChevronDown className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFeature(index);
                    }}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  {expandedFeatureIndex === index ?
                    <ChevronUp className="h-5 w-5" /> :
                    <ChevronDown className="h-5 w-5" />}
                </div>
              </div>

              {expandedFeatureIndex === index && (
                <div className="p-3 border-t">
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Feature Title</label>
                    <input
                      type="text"
                      value={feature.title || ''}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Feature Description</label>
                    <textarea
                      value={feature.description || ''}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                      className="w-full p-2 border rounded"
                      rows="2"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Icon</label>
                    <select
                      value={feature.icon || 'star'}
                      onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      {iconOptions.map((icon) => (
                        <option key={icon} value={icon}>
                          {icon.charAt(0).toUpperCase() + icon.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {feature.icon !== 'no icon' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium mb-1">Icon Color</label>
                          <ColorPicker
                            color={feature.iconColor || content?.iconColor || '#3b82f6'}
                            onChange={(color) => updateFeature(index, 'iconColor', color)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Icon Background Color</label>
                          <ColorPicker
                            color={feature.iconBackgroundColor || content?.iconBackgroundColor || '#e0f2fe'}
                            onChange={(color) => updateFeature(index, 'iconBackgroundColor', color)}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Background Color</label>
                      <ColorPicker
                        color={feature.backgroundColor || content?.cardBackgroundColor || '#ffffff'}
                        onChange={(color) => updateFeature(index, 'backgroundColor', color)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Border Color</label>
                      <ColorPicker
                        color={feature.borderColor || content?.borderColor || '#e5e7eb'}
                        onChange={(color) => updateFeature(index, 'borderColor', color)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Color</label>
                      <ColorPicker
                        color={feature.titleColor || content?.featureTitleColor || '#1f2937'}
                        onChange={(color) => updateFeature(index, 'titleColor', color)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description Color</label>
                      <ColorPicker
                        color={feature.descriptionColor || content?.featureDescriptionColor || '#6b7280'}
                        onChange={(color) => updateFeature(index, 'descriptionColor', color)}
                      />
                    </div>
                  </div>

                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Border Width</label>
                      <input
                        type="text"
                        value={feature.borderWidth || '1px'}
                        onChange={(e) => updateFeature(index, 'borderWidth', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 1px, 2px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Border Radius</label>
                      <input
                        type="text"
                        value={feature.borderRadius || content?.cardBorderRadius || '0.75rem'}
                        onChange={(e) => updateFeature(index, 'borderRadius', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 0.5rem, 8px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Border Style</label>
                      <select
                        value={feature.borderStyle || 'solid'}
                        onChange={(e) => updateFeature(index, 'borderStyle', e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                        <option value="double">Double</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Padding</label>
                      <input
                        type="text"
                        value={feature.padding || '1.5rem'}
                        onChange={(e) => updateFeature(index, 'padding', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 1.5rem, 24px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Margin</label>
                      <input
                        type="text"
                        value={feature.margin || '0'}
                        onChange={(e) => updateFeature(index, 'margin', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 1rem, 16px"
                      />
                    </div>
                  </div>

                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id={`feature-shadow-${index}`}
                      checked={feature.boxShadow !== false}
                      onChange={(e) => updateFeature(index, 'boxShadow', e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor={`feature-shadow-${index}`} className="text-sm">Enable Box Shadow</label>
                  </div>

                  {feature.boxShadow !== false && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Custom Shadow</label>
                      <input
                        type="text"
                        value={feature.customShadow || '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                        onChange={(e) => updateFeature(index, 'customShadow', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Text Alignment</label>
                      <select
                        value={feature.textAlign || 'center'}
                        onChange={(e) => updateFeature(index, 'textAlign', e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Icon Size</label>
                      <select
                        value={feature.iconSize || content?.iconSize || 'large'}
                        onChange={(e) => updateFeature(index, 'iconSize', e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                        <option value="extra-large">Extra Large</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Font Size</label>
                      <input
                        type="text"
                        value={feature.titleFontSize || '1.25rem'}
                        onChange={(e) => updateFeature(index, 'titleFontSize', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 1.25rem, 20px"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description Font Size</label>
                      <input
                        type="text"
                        value={feature.descriptionFontSize || '1rem'}
                        onChange={(e) => updateFeature(index, 'descriptionFontSize', e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., 1rem, 16px"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Font Weight</label>
                      <select
                        value={feature.titleFontWeight || 'medium'}
                        onChange={(e) => updateFeature(index, 'titleFontWeight', e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="normal">Normal</option>
                        <option value="medium">Medium</option>
                        <option value="semibold">Semi Bold</option>
                        <option value="bold">Bold</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description Font Weight</label>
                      <select
                        value={feature.descriptionFontWeight || 'normal'}
                        onChange={(e) => updateFeature(index, 'descriptionFontWeight', e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="light">Light</option>
                        <option value="normal">Normal</option>
                        <option value="medium">Medium</option>
                        <option value="semibold">Semi Bold</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id={`feature-hover-${index}`}
                      checked={feature.hoverEffect !== false}
                      onChange={(e) => updateFeature(index, 'hoverEffect', e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor={`feature-hover-${index}`} className="text-sm">Enable Hover Effect</label>
                  </div>

                  {feature.hoverEffect !== false && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Hover Background Color</label>
                        <ColorPicker
                          color={feature.hoverBackgroundColor || '#f3f4f6'}
                          onChange={(color) => updateFeature(index, 'hoverBackgroundColor', color)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Hover Transform</label>
                        <select
                          value={feature.hoverTransform || 'scale'}
                          onChange={(e) => updateFeature(index, 'hoverTransform', e.target.value)}
                          className="w-full p-2 border rounded"
                        >
                          <option value="none">None</option>
                          <option value="scale">Scale Up</option>
                          <option value="translateY">Move Up</option>
                          <option value="rotate">Rotate</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          className="mt-4 flex items-center justify-center w-full p-2 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400"
          onClick={addFeature}
        >
          <Plus className="h-5 w-5 mr-1" />
          Add Feature
        </button>
      </div>

      {/* Styling options */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Styling</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Background Color</label>
          <ColorPicker
            color={content?.backgroundColor || '#ffffff'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Feature Cards Per Row</label>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1">Mobile</label>
              <select
                value={content?.columnsPerRow?.mobile || 1}
                onChange={(e) => updateContent('columnsPerRow', {
                  ...content?.columnsPerRow,
                  mobile: parseInt(e.target.value)
                })}
                className="w-full p-2 border rounded"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1">Tablet</label>
              <select
                value={content?.columnsPerRow?.tablet || 2}
                onChange={(e) => updateContent('columnsPerRow', {
                  ...content?.columnsPerRow,
                  tablet: parseInt(e.target.value)
                })}
                className="w-full p-2 border rounded"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1">Desktop</label>
              <select
                value={content?.columnsPerRow?.desktop || 4}
                onChange={(e) => updateContent('columnsPerRow', {
                  ...content?.columnsPerRow,
                  desktop: parseInt(e.target.value)
                })}
                className="w-full p-2 border rounded"
              >
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
              </select>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="mb-4 text-blue-600 hover:text-blue-800 font-medium flex items-center"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          {showAdvancedSettings ? (
            <>
              <ChevronUp className="h-5 w-5 mr-1" />
              Hide Advanced Settings
            </>
          ) : (
            <>
              <ChevronDown className="h-5 w-5 mr-1" />
              Show Advanced Settings
            </>
          )}
        </button>

        {showAdvancedSettings && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Feature Title Color</label>
                <ColorPicker
                  color={content?.featureTitleColor || '#1f2937'}
                  onChange={(color) => updateContent('featureTitleColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Feature Description Color</label>
                <ColorPicker
                  color={content?.featureDescriptionColor || '#6b7280'}
                  onChange={(color) => updateContent('featureDescriptionColor', color)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Icon Color</label>
                <ColorPicker
                  color={content?.iconColor || '#3b82f6'}
                  onChange={(color) => updateContent('iconColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Icon Background Color</label>
                <ColorPicker
                  color={content?.iconBackgroundColor || '#e0f2fe'}
                  onChange={(color) => updateContent('iconBackgroundColor', color)}
                />
              </div>
            </div>

            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="card-border"
                checked={content?.borderEnabled !== false}
                onChange={(e) => updateContent('borderEnabled', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="card-border" className="text-sm">Show Card Border</label>
            </div>

            {content?.borderEnabled !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Border Color</label>
                <ColorPicker
                  color={content?.borderColor || '#e5e7eb'}
                  onChange={(color) => updateContent('borderColor', color)}
                />
              </div>
            )}

            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="card-shadow"
                checked={content?.cardShadow !== false}
                onChange={(e) => updateContent('cardShadow', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="card-shadow" className="text-sm">Show Card Shadow</label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Card Background Color</label>
              <ColorPicker
                color={content?.cardBackgroundColor || '#ffffff'}
                onChange={(color) => updateContent('cardBackgroundColor', color)}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Card Border Radius</label>
              <input
                type="text"
                value={content?.cardBorderRadius || '0.75rem'}
                onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g., 0.5rem, 8px"
              />
            </div>

            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="align-center"
                checked={content?.alignCenter !== false}
                onChange={(e) => updateContent('alignCenter', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="align-center" className="text-sm">Center Align Feature Content</label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Icon Size</label>
              <select
                value={content?.iconSize || 'large'}
                onChange={(e) => updateContent('iconSize', e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="show-divider"
                checked={content?.showDivider !== false}
                onChange={(e) => updateContent('showDivider', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="show-divider" className="text-sm">Show Divider Below Title</label>
            </div>

            {content?.showDivider !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Divider Color</label>
                <ColorPicker
                  color={content?.dividerColor || '#e5e7eb'}
                  onChange={(color) => updateContent('dividerColor', color)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Spacing & Animation */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Spacing & Animation</h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Top Padding</label>
            <input
              type="text"
              value={content?.paddingTop || '4rem'}
              onChange={(e) => updateContent('paddingTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Padding</label>
            <input
              type="text"
              value={content?.paddingBottom || '4rem'}
              onChange={(e) => updateContent('paddingBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Margin</label>
            <input
              type="text"
              value={content?.marginBottom || '2rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 2rem, 32px"
            />
          </div>
        </div>

        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="animation-enabled"
            checked={content?.animationEnabled !== false}
            onChange={(e) => updateContent('animationEnabled', e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="animation-enabled" className="text-sm">Enable Animation</label>
        </div>

        {content?.animationEnabled !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Animation Delay (ms)</label>
            <input
              type="number"
              value={content?.animationDelay || 100}
              onChange={(e) => updateContent('animationDelay', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
              min="0"
              max="1000"
              step="50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Delay between each feature card animation (in milliseconds)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturesForm;